import Rectangle from '@/helper-classes/inspector/Rectangle';
import ChartData from '@/helper-classes/inspector/ChartData';

export default class DataPanel {
  id: string;
  chartData: ChartData;
  isPriceData: boolean;
  isFullQuotePanel: boolean;
  isLogarithmic: boolean;
  isPricePanel: boolean;
  isVolumePanel: boolean;
  bounds: Rectangle;
  maxY: number; // Uppermost y value, will also be smallest numerically
  minY: number; // Bottommost y value, will also be greatest numerically
  maxValue: number;
  minValue: number;
  heightToRange: number; // How many pixels on an arithmetic scale to move 1 y value

  constructor(chartData: ChartData, panelString: string, yValues: number[]) {
    const panelStringParts = panelString.split(' ');

    this.id = panelStringParts[1].toUpperCase();
    this.chartData = chartData;

    // If there are 4 panelStringParts, then this must be a ratio chart
    // INTC:D:PRICE,L (normal symbol)
    // $HUI:$GOLD:D:PRICE,L (ratio symbol)
    const idParts = this.id.split(':');
    const panelType = idParts[idParts.length - 1];

    this.isPriceData = panelType.indexOf('PRICE') !== -1;
    this.isFullQuotePanel = panelType.toUpperCase().indexOf('BIGQ') !== -1;
    this.isLogarithmic = this.isPriceData && panelType.substring(6, 7) === 'L';
    this.isPricePanel = panelStringParts.length > 6 && panelStringParts[6].toUpperCase() === 'Y';
    this.isVolumePanel = panelType.toUpperCase() === 'Z';
    this.maxY = parseInt(panelStringParts[2], 10);
    this.minY = parseInt(panelStringParts[3], 10);
    this.maxValue = parseFloat(panelStringParts[4]);
    this.minValue = parseFloat(panelStringParts[5]);

    const range = this.maxValue - this.minValue;

    if (range !== 0) {
      this.heightToRange = Math.abs((this.minY - this.maxY) / range);
    } else {
      this.heightToRange = 1000000;
    }

    const topY = yValues[0];
    const nextY = yValues[1];

    this.bounds = new Rectangle(
      this.chartData.panelRectangle.x + 1,
      topY,
      this.chartData.panelRectangle.width - 1,
      nextY - topY
    );
  }

  public getNumberOfDecimalPlaces() {
    const increment = (this.maxValue - this.minValue) / 20;

    if (increment < 0.01) {
      return 4;
    }

    if (increment < 0.10) {
      return 3;
    }

    return 2;
  }

  public getValueForY(y: number) {
    const yDelta = this.minY - y;

    if (this.isLogarithmic) {
      return this.minValue * Math.exp((yDelta / (this.minY - this.maxY)) * Math.log(this.maxValue / this.minValue));
    }

    return (yDelta / (this.minY - this.maxY)) * (this.maxValue - this.minValue) + this.minValue;
  }

  public getYForValue(value: number) {
    let yDelta = 0;

    if (this.isLogarithmic) {
      if (value === 0) {
        value = 0.01;
      }

      yDelta = (Math.log(value / this.minValue) / Math.log(this.maxValue / this.minValue)) * (this.minY - this.maxY);
    } else {
      yDelta = ((value - this.minValue) / (this.maxValue - this.minValue)) * (this.minY - this.maxY);
    }

    return Math.round(this.minY - yDelta);
  }
}
